import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import config from './CognitoUserPool';
import { CognitoJwtVerifier } from 'aws-jwt-verify';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Move verifier creation outside of component body to prevent recreating on each render
  const verifier = React.useMemo(() => CognitoJwtVerifier.create({
    userPoolId: config.cognito.USER_POOL_ID,
    tokenUse: "id",
    clientId: config.cognito.CLIENT_ID,
  }), []);

  const isTokenValid = useCallback(async (token) => {
    if (!token) return false;

    try {
      const payload = await verifier.verify(token);
      console.log("Token is valid. Payload:", payload);
      return true;
    } catch (error) {
      console.log("Token not valid!", error);
      return false;
    }
  }, [verifier]);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('idToken');
    const isValid = await isTokenValid(token);
    setIsAuthenticated(isValid);
    return isValid;
  }, [isTokenValid]);

  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      try {
        await checkAuth();
      } catch (error) {
        console.error('Authentication check failed:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [checkAuth]);

  const login = useCallback(async () => {
    // Add token verification here before setting isAuthenticated
    await checkAuth();
  }, [checkAuth]);

  const logout = useCallback(() => {
    console.log("Logging out");
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    setIsAuthenticated(false);
  }, []);

  const initiateSignIn = () => {
    // This function will be called when the user clicks the sign-in button
    const clientId = config.cognito.CLIENT_ID;
    const redirectUri = config.cognito.REDIRECT_URI;
    const cognitoDomain = config.cognito.COGNITO_DOMAIN;

    if (!clientId || !redirectUri || !cognitoDomain) {
      console.error('Missing Cognito configuration');
      return;
    }

    const signInUrl = `https://${cognitoDomain}.auth.us-east-1.amazoncognito.com/oauth2/authorize?` +
      `client_id=${clientId}&` +
      `response_type=code&` +
      `scope=${encodeURIComponent('openid profile email')}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}`;

    window.location.href = signInUrl;
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      checkAuth,
      login,
      logout,
      initiateSignIn
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
